import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from 'src/app/models/data/user.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DataFunctions } from 'src/app/tools/data-functions';
import { EmbeddedAlertComponent } from '../embedded-alert/embedded-alert.component';

@Component({
  selector: 'app-password-change-form',
  templateUrl: './password-change-form.component.html',
  styleUrls: ['./password-change-form.component.css'],
  imports: [CommonModule, ReactiveFormsModule, EmbeddedAlertComponent],
})
export class PasswordChangeFormComponent implements OnInit {
  @Input() user!: User;
  @Input() recoveryToken!: string;
  @Input() oldPassword: string = '';
  @Input() passwordRecovery: boolean = false;
  @Input() changePasswordAtLogin: boolean = false;
  @Output() passwordChanged = new EventEmitter<any>();

  passwordForm!: FormGroup;
  error!: string | null;
  success: any;
  changeSuccess: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
  ) {
    this.recoveryToken = DataFunctions.nvl(this.recoveryToken);
    if (this.recoveryToken.length > 0) {
      this.passwordRecovery = true;

      this.authService.checkStatus().subscribe((loginResponseData) => {
        if (loginResponseData) {
          //if logged in, go to home
          if (loginResponseData.status == 1) {
            this.router.navigate(['home']);
          }
        }
      });
    }
  }

  ngOnInit(): void {
    this.passwordForm = this.fb.group({
      oldPassword: [this.oldPassword || '', []],
      password: ['', [Validators.required, Validators.minLength(7)]],
      newPassword: ['', [Validators.required, Validators.minLength(7)]]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(form: AbstractControl) {
    const password = form.get('password');
    const newPassword = form.get('newPassword');

    if (password && newPassword && password.value !== newPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  onSubmit() {
    this.error = null;

    if (this.passwordForm.invalid) {
      // Check for specific validation errors
      const passwordControl = this.passwordForm.get('password');
      const newPasswordControl = this.passwordForm.get('newPassword');
      const oldPasswordControl = this.passwordForm.get('oldPassword');

      if (passwordControl?.hasError('minlength') || newPasswordControl?.hasError('minlength')) {
        this.error = 'Password must be at least 7 characters';
        return;
      }

      // Check if required fields are empty
      if (!passwordControl?.value || !newPasswordControl?.value) {
        this.error = 'Fill in all fields';
        return;
      }

      // If not in password recovery mode, check old password
      if (!this.passwordRecovery && !oldPasswordControl?.value) {
        this.error = 'Fill in all fields';
        return;
      }
    }

    const { password, newPassword, oldPassword } = this.passwordForm.value;

    if (this.passwordForm.hasError('passwordMismatch')) {
      this.error = "Passwords don't match!!";
      return;
    }

    let authentication: string = '';
    if (this.passwordRecovery) {
      authentication = this.recoveryToken;
    } else {
      authentication = DataFunctions.nullOrEmpty(oldPassword) ? this.oldPassword : oldPassword;
    }

    //recover forgotten password
    if (this.passwordRecovery) {
      this.authService
        .changePasswordWithRecoveryToken(
          this.recoveryToken,
          newPassword,
        )
        .subscribe(
          (res) => {
            if (res == null) {
              this.changeSuccess = true;
              this.success = 'Password successfully changed!';
              this.passwordForm.reset();
              this.passwordChanged.emit();
            }
          },
          (errorMessage: any) => {
            if (errorMessage) {
              this.error = 'Invalid Recovery Token';
            }
          },
        );
      //change password by providing the "old"/current password
    } else {
      this.authService
        .changePassword(
          authentication,
          newPassword,
        )
        .subscribe(
          (res) => {
            if (res == null) {
              this.changeSuccess = true;
              this.success = 'Password successfully changed!';
              this.passwordForm.reset();
              this.passwordChanged.emit();
            }
          },
          (errorMessage: any) => {
            if (errorMessage) {
              this.error = 'Invalid Login';
            }
          },
        );
    }
  }

  goToLogin(): void {
    this.router.navigate(['']);
  }
}
